import {IQuestionAndPrompts} from '@chancer/common/lib/core/state/model/CompetitionModel';
import {
  TFirebaseComp,
  TFirebaseQuestion,
} from '@chancer/common/lib/interfaces/firestore/FirestoreClientInterfaces';
import {
  CompQuestionType,
  QuestionStatus,
  TAiPrompt,
  TCompCounts,
  TCompQuestion,
  TJunctionCompetitionsQuestions,
  TLeaderboard,
} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';
import {TTrivia} from '@chancer/common/lib/interfaces/firestore/PredictionInterfaces';
import {
  convert,
  convertClientDocument,
} from '@chancer/common/lib/utils/FirebaseUtils';
import {assertType} from '@chancer/common/lib/utils/TypeUtils';
import firebase from 'firebase/compat/app';

const getQuestionAndPrompts = (
  item: TCompQuestion | IQuestionAndPrompts,
): IQuestionAndPrompts =>
  typeof item === 'object' &&
  item !== null &&
  'captionPrompt' in item &&
  'questionPrompt' in item
    ? item
    : {question: item, captionPrompt: null, questionPrompt: null};

export const addCompetitionQuestions = async (
  batch: firebase.firestore.WriteBatch,
  competitionId: string,
  questions: TCompQuestion[] | IQuestionAndPrompts[],
  config: {updateNumQuestions?: boolean; overwriteStatus?: boolean} = {},
) => {
  const {updateNumQuestions = false, overwriteStatus = true} = config;
  const db = firebase.firestore();
  const questionsCol = db.collection('questions');
  const junctionCompetitionQuestionCol = db.collection(
    'junction_competitions_questions',
  );
  // TODO: Use count() when we have updated to the modular SDK.
  const indexStartCount = (
    await junctionCompetitionQuestionCol
      .where('competitionId', '==', competitionId)
      .get()
  ).size;
  for (let i = 0; i < questions.length; i++) {
    const item = getQuestionAndPrompts(questions[i]);
    const question = item.question;
    const captionPrompt = item.captionPrompt;
    const questionPrompt = item.questionPrompt;
    const resolved = item.resolved;
    const questionRef = questionsCol.doc();

    const junctionCompetitionQuestionRef = junctionCompetitionQuestionCol.doc(
      `${competitionId}_${questionRef.id}`,
    );
    batch.set(
      junctionCompetitionQuestionRef,
      assertType<TJunctionCompetitionsQuestions>({
        competitionId: competitionId,
        questionId: questionRef.id,
        questionIndex: indexStartCount + i,
        status: overwriteStatus ? QuestionStatus.DRAFT : question.status,
      }),
    );
    batch.set(
      questionRef,
      assertType<TCompQuestion>({
        ...question,
        ...(overwriteStatus ? {status: QuestionStatus.DRAFT} : {}),
      }),
    );
    if (captionPrompt !== null) {
      batch.set(
        questionRef.collection('question_data').doc('prompt'),
        assertType<TAiPrompt>({
          created: firebase.firestore.FieldValue.serverTimestamp() as any,
          updated: firebase.firestore.FieldValue.serverTimestamp() as any,
          prompt: captionPrompt?.prompt,
        }),
      );
    }
    if (questionPrompt !== null) {
      batch.set(
        questionRef.collection('question_data').doc('prompt_question'),
        assertType<TAiPrompt>({
          created: firebase.firestore.FieldValue.serverTimestamp() as any,
          updated: firebase.firestore.FieldValue.serverTimestamp() as any,
          prompt: questionPrompt?.prompt,
        }),
      );
    }
    if (updateNumQuestions || resolved !== undefined) {
      batch.update(
        db
          .collection('competitions')
          .doc(competitionId)
          .collection('comp_data')
          .doc('counts'),
        {
          ...(updateNumQuestions
            ? {numQuestions: firebase.firestore.FieldValue.increment(1)}
            : {}),
          ...(resolved !== undefined
            ? {
                resolutions: firebase.firestore.FieldValue.arrayUnion({
                  id: questionRef.id,
                  resolved,
                }),
              }
            : {}),
        },
      );
    }
  }
};

export const removeAllQuestionsFromComp = async (
  batch: firebase.firestore.WriteBatch,
  competitionId: string,
) => {
  const db = firebase.firestore();
  const junctionCompetitionQuestion = await db
    .collection('junction_competitions_questions')
    .where('competitionId', '==', competitionId)
    .get();
  const questionIds = junctionCompetitionQuestion.docs.map(
    (doc) => (doc.data() as TJunctionCompetitionsQuestions).questionId,
  );
  await removeCompetitionQuestions(batch, competitionId, questionIds);
};

export const removeCompetitionQuestions = async (
  batch: firebase.firestore.WriteBatch,
  competitionId: string,
  questionIds: string[],
) => {
  const db = firebase.firestore();
  const questionsCol = db.collection('questions');
  const junctionCompetitionQuestionCol = db.collection(
    'junction_competitions_questions',
  );
  for (const questionId of questionIds) {
    const counts = await junctionCompetitionQuestionCol
      .where('questionId', '==', questionId)
      .limit(2)
      .get();

    // If we have only one junction ref this question is not going to be used any more, delete
    if (counts.size === 1) {
      batch.delete(questionsCol.doc(questionId));
    }

    batch.delete(
      junctionCompetitionQuestionCol.doc(`${competitionId}_${questionId}`),
    );
    batch.update(
      db
        .collection('competitions')
        .doc(competitionId)
        .collection('comp_data')
        .doc('counts'),
      {
        numQuestions: firebase.firestore.FieldValue.increment(-1),
      },
    );
  }
};

export const isMiniLeague = async (leaderboards: string[]) =>
  (
    await firebase
      .firestore()
      .collection('leaderboards')
      .where(firebase.firestore.FieldPath.documentId(), 'in', leaderboards)
      .get()
  ).docs
    .map((d) => convert(TLeaderboard, d))
    .some((l) => l.isMiniLeague && l.published);

export const getRecentTriviaQuestions = async (
  vendor: string,
): Promise<TTrivia[]> => {
  const db = firebase.firestore();
  const competitions = (
    await db
      .collection('competitions')
      .where('vendor', '==', vendor)
      .orderBy('starts', 'desc')
      .limit(10)
      .get()
  ).docs.map((d) => convertClientDocument(TFirebaseComp, d));
  if (competitions.length === 0) {
    return [];
  }

  const compCounts = await Promise.all(
    competitions.map(async (comp) => {
      const countsRef = await db
        .collection('competitions')
        .doc(comp.id)
        .collection('comp_data')
        .doc('counts')
        .get();
      return convert(TCompCounts, countsRef);
    }),
  );
  const questionIds = Array.from(
    new Set(compCounts.flatMap((c) => c.resolutions || []).map((r) => r.id)),
  );
  if (questionIds.length === 0) {
    return [];
  }

  const resolutions = new Map(
    compCounts
      .flatMap((c) => c.resolutions || [])
      .map((r) => [r.id, r.resolved]),
  );
  return (
    await db
      .collection('questions')
      .where(
        firebase.firestore.FieldPath.documentId(),
        'in',
        questionIds.slice(0, 30),
      )
      .get()
  ).docs
    .map((doc) => convertClientDocument(TFirebaseQuestion, doc))
    .filter(
      (question) =>
        question.type === CompQuestionType.TRIVIA &&
        resolutions.has(question.id),
    )
    .map((question) => ({
      question: question.question,
      answers: question.answers.map((a) => a.label),
      correctAnswerIndex: resolutions.get(question.id) as number,
    }))
    .slice(0, 25);
};
